import { createFormDataFromItem, createItemFromFormData, getFormDefinition } from './form';
import { entityToStore } from '@/utils/store';
import baseCrudMixin from '@/mixins/crud';
import { FinishTypeOption, OrderLineSubStatusOption, ProductGroupTypeOption } from '@/utils/constants';
import { BottomHemChoiceOption } from '@/utils/constants';

export default {
  computed: {
    definition() {
      return this.createDefinition();
    },
    entityStore() {
      return entityToStore(this.entity);
    },
    productGroupTypeOption() {
      return this.$store.getters['editor/value']('productGroupTypeOption');
    },
    liningApplicable() {
      const fabricProductID = this.$store.getters['editor/value']('liningFabricProductID');
      return !!fabricProductID;
    },
    productGroupHasPanels() {
      let hasPanels = false;
      switch (this.productGroupTypeOption) {
        case ProductGroupTypeOption.Curtain:
        case ProductGroupTypeOption.NetCurtain:
        case ProductGroupTypeOption.RomanBlind:
          hasPanels = true;
          break;
        default:
          break;
      }
      return hasPanels;
    },
    orderIsCancelled() {
      return this.orderHasStatus(OrderLineSubStatusOption.Cancelled);
    },
    orderIsInProductionOrCancelled() {
      const orderLineStatusID = this.$store.getters['editor/value']('orderLineStatusID');
      const orderLineStatus = this.$store.getters['orderLineStatus/getLoadedItemByID'](orderLineStatusID);
      return (
        orderLineStatus &&
        orderLineStatus.orderLineSubstatusOption !== OrderLineSubStatusOption.NoStock &&
        orderLineStatus.orderLineSubstatusOption !== OrderLineSubStatusOption.Selectable &&
        orderLineStatus.orderLineSubstatusOption !== OrderLineSubStatusOption.Unprocessable
      );
    },
    orderIsPrintable() {
      // todo check for more conditions that determine if this order is printable
      const form = this.$store.getters['editor/flattenedForm'];
      return !!form.plannedWorkshopID;
    },
    orderIsUnprocessable() {
      return this.orderHasStatus(OrderLineSubStatusOption.Unprocessable);
    }
  },
  mixins: [baseCrudMixin],
  data: () => ({
    entity: 'OrderLine',
    returnTo: 'OrderLines',
    injectFormValues: {},
    printDialogVisible: false
  }),
  created() {
    this.$store.dispatch('store/getItems');
    this.$store.dispatch('orderLineStatus/getItems');
    this.$store.dispatch('returnCause/getItems');
    this.$store.dispatch('priceListItem/getItems');
    this.$store.dispatch('country/getItems');
  },
  methods: {
    formHasChanged() {
      return false;
    },
    createDefinition() {
      return getFormDefinition(this);
    },
    orderHasStatus(orderLineSubstatusOption) {
      const orderLineStatusID = this.$store.getters['editor/value']('orderLineStatusID');
      const orderLineStatus = this.$store.getters['orderLineStatus/getLoadedItemByID'](orderLineStatusID);
      return orderLineStatus && orderLineStatus.orderLineSubstatusOption === orderLineSubstatusOption;
    },
    handleChildValue(value) {
      this.injectFormValues = Object.assign({}, { returnReasonID: value });
    },
    productGroupHasFixtures() {
      let hasFixtures = false;
      switch (this.productGroupTypeOption) {
        case ProductGroupTypeOption.Curtain:
        case ProductGroupTypeOption.NetCurtain:
        case ProductGroupTypeOption.CurtainRoomHigh:
          hasFixtures = true;
          break;
      }
      return hasFixtures;
    },
    hasFinishTypeOperation() {
      const details = this.$store.getters['editor/value']('orderLineDetails');
      return details && details.some(d => d.finishFinishTypeFinishTypeOption === FinishTypeOption.Operation);
    },
    isProductGroupType(productGroupTypeOption) {
      return this.productGroupTypeOption === productGroupTypeOption;
    },
    getFinishedHeightProps(isEdit, returnOrderLineYN) {
      const props = {
        readonly: isEdit && !returnOrderLineYN,
        caption: 'orderLine.height',
        precision: 1,
        step: 0.5
      };
      switch (this.productGroupTypeOption) {
        case ProductGroupTypeOption.Cutting:
          props['caption'] = 'orderLine.length';
          props['max'] = 3000;
          break;
        case ProductGroupTypeOption.Embrace:
          props['visible'] = false;
          break;
      }
      return props;
    },
    getNumberOfPanelsPropsBySideProps(side, forLining) {
      let visible = this.productGroupHasPanels;
      if (visible && forLining) {
        visible = this.liningApplicable;
      }
      return {
        visible: visible,
        caption: `orderLine.numberOfPanels${side}`,
        precision: 2,
        step: 0.25
      };
    },
    getFinishedWidthBySideProps(side, width, isEdit, returnOrderLineYN) {
      const props = {
        visible: false,
        readonly: isEdit && !returnOrderLineYN
      };
      switch (this.productGroupTypeOption) {
        case ProductGroupTypeOption.Curtain:
        case ProductGroupTypeOption.NetCurtain:
        case ProductGroupTypeOption.CurtainRoomHigh:
          props['precision'] = 1;
          props['step'] = 0.5;
          props['caption'] = `orderLine.width${side}`;
          props['visible'] = true;
          break;
        case ProductGroupTypeOption.RomanBlind:
          if (width) {
            props['precision'] = 1;
            props['step'] = 0.5;
            props['caption'] = `orderLine.width`;
            props['visible'] = true;
          }
          break;
        default:
          break;
      }
      return props;
    },
    getHeadingHeightProps() {
      const props = {
        visible: false,
        caption: 'orderLine.headingHeight'
      };
      switch (this.productGroupTypeOption) {
        case ProductGroupTypeOption.Curtain:
        case ProductGroupTypeOption.NetCurtain:
        case ProductGroupTypeOption.CurtainRoomHigh:
          props['precision'] = 1;
          props['step'] = 0.5;
          props['visible'] = true;
          break;
        default:
          break;
      }
      return props;
    },
    getCutSize(form) {
      return form.cutSizeLeft ? form.cutSizeLeft : form.cutSizeRight;
    },
    getCutSizeLining(form) {
      return form.cutSizeLeftLining ? form.cutSizeLeftLining : form.cutSizeRightLining;
    },
    getNumberOfPanels(form) {
      return +form.numberOfPanelsLeft + +form.numberOfPanelsRight;
    },
    getNumberOfPanelsLining(form) {
      return +form.numberOfPanelsLeftLining + +form.numberOfPanelsRightLining;
    },
    getRemarksOnTicket(form) {
      const remarks = [];
      if (form.alignPatternYN !== null && form.alignPatternYN) {
        remarks.push(this.$i18n.t('orderLine.alignPatternYN'));
      }
      if (form.alignPatternTopYN) {
        remarks.push(this.$i18n.t('orderLine.alignPatternTopYN'));
      }
      if (form.takeLeftoverFabricYN !== null && form.takeLeftoverFabricYN) {
        remarks.push(this.$i18n.t('orderLine.takeLeftoverFabric'));
      }
      const bottomHemChoiceOption = this.$store.getters['option/loadedOptions']('bottomHemChoice').find(
        o => o.code === form.bottomHemChoice
      );
      if (bottomHemChoiceOption && bottomHemChoiceOption.code !== BottomHemChoiceOption.DefaultHem) {
        remarks.push(`${bottomHemChoiceOption.optionGroupDescription}: ${bottomHemChoiceOption.description}`);
      }
      return remarks.join(', ');
    },
    async selectPrinterClient() {
      this.$store
        .dispatch('notify/confirm', { message: this.$i18n.t('orderLine.confirmPrint') })
        .then(() => {
          this.printDialogVisible = true;
        })
        .catch(() => {
          console.log('print cancelled by user');
        });
    },
    async printerClientSelected(printerClientID) {
      this.printDialogVisible = false;
      return this.printProductionTicket(printerClientID);
    },
    printerClientSelectionCancelled() {
      this.printDialogVisible = false;
    },
    async printProductionTicket(printerClientID) {
      const form = this.$store.getters['editor/flattenedForm'];
      const printCommand = {
        orderLineID: form.id,
        printerClientID: printerClientID
      };
      await this.$store.dispatch('orderLine/printProductionTicket', printCommand);
      this.$store.dispatch('notify/success', { message: this.$i18n.t('orderLine.productionTicketPrinted') });
    },
    async createReturnOrderLine() {
      const form = this.$store.getters['editor/flattenedForm'];
      this.$router.push({ name: 'CreateOrderLine', params: { id: form.id } });
    },
    createFormDataFromItem,
    createItemFromFormData
  }
};
